import type { ComponentContentSchema, SlotGroupDefinition } from '@vibesites/runtime';
import {
  useSlotGroup,
  useContentToken,
  EditableSlotGroup,
  RichTextContent,
} from '@vibesites/runtime/react';
import { VibesitesImage } from '@vibesites/ui-primitives';
import { CountUp } from './CountUp';
import { plainText } from './propertyUtils';

const bgDef: SlotGroupDefinition = {
  label: 'Background Image',
  fields: {
    image: {
      label: 'Image',
      type: 'image',
      defaultValue:
        '',
    },
    alt: {
      label: 'Alt Text',
      type: 'text',
      defaultValue: 'American Landmark property exterior',
    },
  },
};

const ctaDef: SlotGroupDefinition = {
  label: 'CTA Link',
  fields: {
    url: {
      label: 'URL',
      type: 'url',
      defaultValue: '/about-us',
    },
    label: {
      label: 'Link Text',
      type: 'text',
      defaultValue: 'learn more about us',
    },
    _target: {
      label: 'Target',
      type: 'text',
      defaultValue: '_self',
    },
  },
};

export function HomeStats() {
  const bg = useSlotGroup('homestats.background', bgDef);
  const cta = useSlotGroup('homestats.cta', ctaDef);
  const value1 = plainText(useContentToken('homestats.stat_1_value'));
  const value2 = plainText(useContentToken('homestats.stat_2_value'));
  const value3 = plainText(useContentToken('homestats.stat_3_value'));
  const value4 = plainText(useContentToken('homestats.stat_4_value'));
  const value5 = plainText(useContentToken('homestats.stat_5_value'));

  return (
    <section
      className="relative px-4 py-20 text-[rgb(var(--ala-on-primary))] md:px-8 md:py-32"
      style={{ backgroundColor: 'rgb(var(--ala-dark))' }}
    >
      <EditableSlotGroup groupPath="homestats.background" definition={bgDef}>
        <div className="pointer-events-none absolute inset-0 overflow-hidden" aria-hidden>
          <VibesitesImage
            url={
              bg.image ||
              ''
            }
            alt={bg.alt || ''}
            sizes="100vw"
            className="h-full w-full object-cover opacity-20"
          />
        </div>
      </EditableSlotGroup>

      <div className="relative z-10 mx-auto flex max-w-[1440px] flex-col gap-12">
        <div className="flex max-w-[820px] flex-col gap-6">
          <div className="flex items-center gap-2">
            <span className="h-2 w-2 shrink-0 rounded-full bg-[rgb(var(--ala-accent))]" aria-hidden />
            <RichTextContent
              path="homestats.eyebrow"
              label="Eyebrow"
              as="span"
              className="font-heading text-sm font-medium uppercase tracking-[-0.3px] text-[rgb(var(--ala-on-primary))]/50"
            />
          </div>
          <RichTextContent
            path="homestats.headline"
            label="Headline"
            as="h2"
            className="font-heading text-[clamp(33px,3.69vw,60px)] font-medium leading-[1.1] tracking-[-0.05em]"
          />
        </div>

        <div className="flex flex-col gap-12 border-t border-white/10 pt-12 lg:flex-row lg:gap-0">
          <div className="mb-4 flex w-full shrink-0 items-start self-start lg:sticky lg:top-48 lg:z-20 lg:mb-0 lg:max-w-[300px]">
            <EditableSlotGroup groupPath="homestats.cta" definition={ctaDef}>
              <a
                href={cta.url || '/about-us'}
                target={cta._target || '_self'}
                className="inline-flex items-center rounded-full bg-[rgb(var(--ala-secondary))] px-6 py-3.5 font-heading text-sm uppercase tracking-wide text-[rgb(var(--ala-on-secondary))] transition hover:bg-[#3d7fd9]"
              >
                {cta.label || 'learn more about us'}
              </a>
            </EditableSlotGroup>
          </div>

          <div className="grid flex-grow grid-cols-1 border-white/10 md:grid-cols-2 lg:border-l">
            <div className="flex flex-col justify-between gap-12 border-b border-white/10 p-8 md:border-r md:border-white/10 md:p-12">
              <RichTextContent
                path="homestats.stat_1_label"
                label="Stat 1 Label"
                as="p"
                className="max-w-[250px] font-heading text-base font-medium leading-snug tracking-[-0.4px] text-[rgb(var(--ala-on-primary))]/50"
              />
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="homestats.stat_1_value" label="Stat 1 Value" as="span" />
                </span>
                <CountUp
                  value={value1 || '0'}
                  className="font-heading text-[clamp(50px,5.54vw,90px)] font-medium leading-none tracking-[-0.06em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>

            <div className="flex flex-col justify-between gap-12 border-b border-white/10 p-8 md:p-12">
              <RichTextContent
                path="homestats.stat_2_label"
                label="Stat 2 Label"
                as="p"
                className="max-w-[250px] font-heading text-base font-medium leading-snug tracking-[-0.4px] text-[rgb(var(--ala-on-primary))]/50"
              />
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="homestats.stat_2_value" label="Stat 2 Value" as="span" />
                </span>
                <CountUp
                  value={value2 || '0'}
                  className="font-heading text-[clamp(50px,5.54vw,90px)] font-medium leading-none tracking-[-0.06em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>

            <div className="flex flex-col justify-between gap-12 border-b border-white/10 p-8 md:border-r md:border-white/10 md:p-12">
              <RichTextContent
                path="homestats.stat_3_label"
                label="Stat 3 Label"
                as="p"
                className="max-w-[250px] font-heading text-base font-medium leading-snug tracking-[-0.4px] text-[rgb(var(--ala-on-primary))]/50"
              />
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="homestats.stat_3_value" label="Stat 3 Value" as="span" />
                </span>
                <CountUp
                  value={value3 || '0'}
                  className="font-heading text-[clamp(50px,5.54vw,90px)] font-medium leading-none tracking-[-0.06em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>

            <div className="flex flex-col justify-between gap-12 border-b border-white/10 p-8 md:p-12">
              <RichTextContent
                path="homestats.stat_4_label"
                label="Stat 4 Label"
                as="p"
                className="max-w-[250px] font-heading text-base font-medium leading-snug tracking-[-0.4px] text-[rgb(var(--ala-on-primary))]/50"
              />
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="homestats.stat_4_value" label="Stat 4 Value" as="span" />
                </span>
                <CountUp
                  value={value4 || '0'}
                  className="font-heading text-[clamp(50px,5.54vw,90px)] font-medium leading-none tracking-[-0.06em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>

            <div className="flex flex-col justify-between gap-12 border-b border-white/10 p-8 md:col-span-2 md:border-r md:border-white/10 md:p-12 lg:col-span-1">
              <RichTextContent
                path="homestats.stat_5_label"
                label="Stat 5 Label"
                as="p"
                className="max-w-[250px] font-heading text-base font-medium leading-snug tracking-[-0.4px] text-[rgb(var(--ala-on-primary))]/50"
              />
              <div className="relative">
                <span className="sr-only">
                  <RichTextContent path="homestats.stat_5_value" label="Stat 5 Value" as="span" />
                </span>
                <CountUp
                  value={value5 || '0'}
                  className="font-heading text-[clamp(50px,5.54vw,90px)] font-medium leading-none tracking-[-0.06em] text-[rgb(var(--ala-on-primary))]"
                />
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>
  );
}

export const contentSchema: ComponentContentSchema = {
  slot: 'HomeStats',
  displayName: 'Home Stats',
  tokens: {
    'homestats.eyebrow': {
      label: 'Eyebrow',
      type: 'richtext',
      defaultValue: '<p>success stats</p>',
    },
    'homestats.headline': {
      label: 'Headline',
      type: 'richtext',
      defaultValue: '<p>Showcasing excellence of our landmark mission</p>',
    },
    'homestats.stat_1_value': {
      label: 'Stat 1 Value',
      type: 'richtext',
      defaultValue: '<p>10,000+</p>',
    },
    'homestats.stat_1_label': {
      label: 'Stat 1 Label',
      type: 'richtext',
      defaultValue: '<p>Providing quality living experiences for thousands of residents.</p>',
    },
    'homestats.stat_2_value': {
      label: 'Stat 2 Value',
      type: 'richtext',
      defaultValue: '<p>30+</p>',
    },
    'homestats.stat_2_label': {
      label: 'Stat 2 Label',
      type: 'richtext',
      defaultValue: '<p>Years of multifamily excellence</p>',
    },
    'homestats.stat_3_value': {
      label: 'Stat 3 Value',
      type: 'richtext',
      defaultValue: '<p>8</p>',
    },
    'homestats.stat_3_label': {
      label: 'Stat 3 Label',
      type: 'richtext',
      defaultValue: '<p>States Served</p>',
    },
    'homestats.stat_4_value': {
      label: 'Stat 4 Value',
      type: 'richtext',
      defaultValue: '<p>500+</p>',
    },
    'homestats.stat_4_label': {
      label: 'Stat 4 Label',
      type: 'richtext',
      defaultValue: '<p>Team members</p>',
    },
    'homestats.stat_5_value': {
      label: 'Stat 5 Value',
      type: 'richtext',
      defaultValue: '<p>35+</p>',
    },
    'homestats.stat_5_label': {
      label: 'Stat 5 Label',
      type: 'richtext',
      defaultValue: '<p>Communities</p>',
    },
  },
  slotGroups: {
    'homestats.background': bgDef,
    'homestats.cta': ctaDef,
  },
};

(HomeStats as unknown as { contentSchema: ComponentContentSchema }).contentSchema = contentSchema;

export default HomeStats;
